import type { ContentFilter, ContentFilterData } from '../../types/webout';

/**
 * Required filter parameters and the single rule for filling them in (DEV-757).
 *
 * The editor used to create every filter as `{ type, data: {} }`. Six of the
 * seven offered types survive that: the player hands the whole `data` object to
 * a PIXI constructor that `Object.assign`s it over its own defaults, so a
 * missing key is simply the default. `COLOR_OVERLAY_FILTER` is the exception —
 * it is the only arm of `addFilters` that reads properties off `data` and
 * passes one to a converter:
 *
 * ```ts
 * new ColorOverlayFilter(utils.string2hex(filter.data.color), filter.data.alpha)
 * ```
 *
 * `string2hex(undefined)` throws `Unable to convert color undefined`, and
 * because `addFilters` is reached from `prepareOnStartup` — which walks every
 * content item, image and scene of every chapter before playback — one filter
 * with an empty `data` anywhere in the project aborts startup for all of it.
 *
 * The defaults below are PIXI's own constructor defaults
 * (`ColorOverlayFilter(color = 0x000000, alpha = 1)`). Seeding anything else
 * would make an untouched Color Overlay render differently from every other
 * untouched filter, which all fall back to their library defaults.
 *
 * This module is pure and Angular-free so the same rule can be unit tested and
 * reused on the read path. The backend mirrors it in
 * `filter-heal.service.ts` — keep the two in step.
 */

/** Values a filter type cannot be rendered without. */
const REQUIRED_DEFAULTS: Record<string, Readonly<Partial<ContentFilterData>>> = {
	COLOR_OVERLAY_FILTER: { color: '#000000', alpha: 1 },
};

/**
 * Filter types the editor once offered that the player has no arm for.
 * `COLOR_OVERLAY` is absent from the player's `FilterTypes` enum and the switch
 * has no `default:` case, so such a filter was saved and then silently dropped
 * at render time — the quiet twin of the crash above. Aliasing on read is
 * kinder to projects that already store it than deleting the filter outright.
 */
const TYPE_ALIASES: Record<string, string> = {
	COLOR_OVERLAY: 'COLOR_OVERLAY_FILTER',
};

/** The canonical player type for a stored filter type. */
export function canonicalFilterType(type: string | undefined): string {
	if (!type) return '';
	return TYPE_ALIASES[type] ?? type;
}

/** A fresh `data` seed for a newly created filter of this type. */
export function defaultFilterData(type: string | undefined): ContentFilterData {
	return { ...(REQUIRED_DEFAULTS[canonicalFilterType(type)] ?? {}) };
}

/**
 * Whether clearing this field would leave the player unable to render the
 * filter. Required fields are restored to their default instead of deleted.
 */
export function isRequiredFilterField(type: string | undefined, key: keyof ContentFilterData): boolean {
	return key in (REQUIRED_DEFAULTS[canonicalFilterType(type)] ?? {});
}

/** The default for one required field, or `undefined` when it is not required. */
export function defaultFilterField(
	type: string | undefined,
	key: keyof ContentFilterData,
): ContentFilterData[keyof ContentFilterData] {
	return REQUIRED_DEFAULTS[canonicalFilterType(type)]?.[key];
}

/**
 * Bring one stored filter up to something the player can render: canonical
 * type, and every required parameter present. Returns the original reference
 * when nothing needed changing.
 */
export function normalizeFilter(filter: ContentFilter): ContentFilter {
	if (!filter || typeof filter !== 'object') return filter;
	const type = canonicalFilterType(filter.type);
	const required = REQUIRED_DEFAULTS[type] ?? {};
	const missing = (Object.keys(required) as (keyof ContentFilterData)[])
		.filter(key => isMissing(filter.data?.[key]));
	if (type === filter.type && !missing.length) return filter;

	const data: ContentFilterData = { ...filter.data };
	for (const key of missing) {
		(data as Record<string, unknown>)[key as string] = required[key];
	}
	return { ...filter, type: type as ContentFilter['type'], data };
}

/** {@link normalizeFilter} over a list, returning the original array when it was already correct. */
export function normalizeFilters<T extends ContentFilter[] | undefined | null>(filters: T): T {
	if (!Array.isArray(filters)) return filters;
	let changed = false;
	const next = filters.map(filter => {
		const normalized = normalizeFilter(filter);
		if (normalized !== filter) changed = true;
		return normalized;
	});
	return (changed ? next : filters) as T;
}

/**
 * A required value counts as missing when it is absent or an empty string.
 * `0` and `false` are real values — treating them as missing is precisely the
 * `if (!value) delete …` mistake that re-created this crash on a working filter.
 */
function isMissing(value: unknown): boolean {
	return value === undefined || value === null || value === '';
}
